<script setup lang="ts">
import { computed } from 'vue';

type ButtonVariant = 'primary' | 'secondary' | 'negative' | 'filter' | 'textOnly';

defineEmits<{ (e: 'click', event: Event): void }>();

const props = defineProps<{
	variant?: ButtonVariant;
	label: string;
	disabled?: boolean;
	isLoading?: boolean;
	iconRight?: boolean;
	iconName?: string;
	size?: 'small' | 'normal';
	buttonType?: 'button' | 'submit';
}>();

const getButtonClass = computed(() => {
	switch (props.variant) {
		case 'secondary':
			return 'button--secondary';
		case 'negative':
			return 'button--negative';
		case 'filter':
			return 'button--filter';
		case 'textOnly':
			return 'button--text-only';
		case 'primary':
		default:
			return 'button--primary';
	}
});
</script>
<template>
	<button
		:class="[
			'button',
			{ 'button--small': size === 'small' },
			{ 'button--loading': isLoading },
			getButtonClass,
		]"
		:type="buttonType || 'button'"
		:disabled="disabled"
		:tabindex="isLoading ? -1 : 0"
		data-ts="btn"
		@click.prevent="$emit('click', $event)"
	>
		<span v-if="isLoading" class="button__spinner" data-ts="btn-spinner" />
		<span
			v-if="iconName && !iconRight && !isLoading"
			:class="[`bx ${iconName} mr-2`, { 'text-lg': size !== 'small' }]"
			data-ts="btn-icon-left"
		/>
		{{ label }}
		<span
			v-if="iconName && iconRight && !isLoading"
			:class="[`bx ${iconName} ml-2`, { 'text-lg': size !== 'small' }]"
			data-ts="btn-icon-right"
		/>
	</button>
</template>
<style lang="scss" scoped>
$spinner-size: 14px;

.button {
	$this: &;
	height: $height-button;
	background-color: $color-secondary-400;
	border: $border-width-1 $border-style $color-secondary-400;
	border-radius: $border-radius-2;
	padding: 0 $spacing-3;
	font-size: $font-size;
	font-weight: $font-weight-medium;
	color: $color-white;
	display: flex;
	align-items: center;
	transform-origin: center center;
	transition: transform 150ms ease-in-out;
	white-space: nowrap;
	user-select: none;
	justify-content: center;

	&--small {
		height: $height-button-small;
		font-size: $font-size-xsm;
		padding: 0 $spacing-2;
	}

	&:disabled {
		background-color: $color-neutral-100;
		border: $border-width-1 $border-style $color-neutral-200;
		color: $color-neutral-400;
		cursor: not-allowed;
		box-shadow: none;
	}

	&:active:not(:disabled) {
		transform: scale(0.95);
	}

	@media (hover: hover) {
		&:hover:not(:disabled) {
			color: $color-white;
			background-color: $color-secondary-500;
			box-shadow: none;
		}
	}

	&:focus-visible {
		outline: $border-width-2 $border-style $color-focus;
	}

	&__spinner {
		width: $spinner-size;
		height: $spinner-size;
		border: $border-width-2 $border-style rgba($color-white, 0.3);
		border-radius: 50%;
		border-top-color: $color-white;
		animation: spin 1s ease-in-out infinite;
		margin-right: $spacing-3;
	}

	&--secondary {
		background-color: $color-white;
		color: $color-gray-800;
		border-color: $color-gray-600;
		#{$this}__spinner {
			border: $border-width-2 $border-style rgba($color-secondary, 0.3);
			border-top-color: $color-secondary;
		}

		@media (hover: hover) {
			&:hover:not(:disabled) {
				color: $color-gray-800;
				background-color: $color-gray-100;
				box-shadow: none;
			}
		}
	}

	&--negative {
		background-color: $color-error-600;
		border-color: $color-error-600;

		@media (hover: hover) {
			&:hover:not(:disabled) {
				border-color: $color-error-700;
				background-color: $color-error-700;
			}
		}
	}

	&--loading {
		pointer-events: none;
	}

	&--filter {
		height: $height-form-element;
		background-color: $color-white;
		color: $color-black;

		@media (hover: hover) {
			&:hover:not(:disabled) {
				background-color: $color-gray-800 !important;
			}
		}
	}

	&--text-only {
		background-color: transparent;
		text-decoration: underline;
		border-color: transparent;
		color: $color-black;
		height: $height-form-element;

		@media (hover: hover) {
			&:hover:not(:disabled) {
				background-color: transparent !important;
				color: $color-black !important;
			}
		}
	}
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}
</style>
